import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Clock, CheckCircle2 } from 'lucide-react';

interface BlueprintPreviewProps {
  result?: {
    prompt: string;
    imageUrl?: string;
    processingTime?: string;
  };
  isGenerating?: boolean;
  isSaved?: boolean;
}

const BlueprintPreview: React.FC<BlueprintPreviewProps> = ({ result, isGenerating, isSaved }) => {
  const previousImageUrl = React.useRef<string | undefined>();

  // Helper function to get full image URL
  const getFullImageUrl = (relativeUrl?: string) => {
    if (!relativeUrl) return '';
    // If it's already a full URL, return as is
    if (relativeUrl.startsWith('http')) return relativeUrl;
    
    // Extract the image key from the path
    const imageKey = relativeUrl.split('/').pop();
    if (!imageKey) return '';
    
    // Use the images endpoint as defined in api.ts
    return `${import.meta.env.VITE_API_URL}/api/images/${imageKey}`;
  };

  React.useEffect(() => {
    if (result?.imageUrl && result.imageUrl !== previousImageUrl.current) {
      previousImageUrl.current = result.imageUrl;
    }
  }, [result?.imageUrl]);

  // Add debug logging
  React.useEffect(() => {
    console.log('BlueprintPreview props:', { result, isGenerating });
  }, [result, isGenerating]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {result ? (
          <div className="space-y-4">
            <div className="aspect-square bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden relative">
              {isGenerating ? (
                <>
                  {previousImageUrl.current && (
                    <img 
                      src={getFullImageUrl(previousImageUrl.current)}
                      alt="Previous preview"
                      className="absolute inset-0 w-full h-full object-contain blur-sm transition-all duration-200"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin">
                        <div className="w-8 h-8 border-4 border-zinc-800 border-t-zinc-400 rounded-full"/>
                      </div>
                      <span className="text-gray-400">Generating image...</span>
                    </div>
                  </div>
                </>
              ) : result.imageUrl ? (
                <>
                  <img 
                    src={getFullImageUrl(result.imageUrl)} 
                    alt="Generated preview"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.error('Image load error:', e);
                    }}
                  />
                  {isSaved && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="w-12 h-12 text-green-500" />
                        <span className="text-gray-200">Blueprint Saved!</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-400">Ready to generate</span>
              )}
              {result.processingTime && !isSaved && (
                <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded-md text-sm flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {result.processingTime}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="aspect-square bg-gray-800 rounded-lg flex items-center justify-center">
            <span className="text-gray-400">Ready to test your blueprint</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BlueprintPreview; 